import LayerGroup from 'ol/layer/Group';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import TileArcGISRest from 'ol/source/TileArcGISRest';
import { boundingExtent } from 'ol/extent';
import { fromLonLat } from 'ol/proj';

import { MAP_PROJECTION, SA_BOUNDS } from 'config';
import { getTileGrid } from 'utils';

import { AbstractLayer, AbstractLayerProps, LayerProps } from './AbstractLayer';

type AnyTileLayer = TileLayer<XYZ> | TileLayer<TileArcGISRest>;

/**
 * AbstractLayer implementation that stacks multiple tile layers into a
 * single logical basemap — e.g. an imagery layer with a label overlay on top.
 *
 * Internally uses an ol/layer/Group so OpenLayers propagates visibility,
 * opacity and zIndex to all children automatically.
 *
 * Children are rendered bottom-up in the order they appear in
 * `config.compositeChildren` — first entry is drawn beneath the rest.
 */
// AbstractLayer's generic is constrained to ol/layer/Layer but LayerGroup
// extends BaseLayer directly. Using `any` here is the pragmatic escape —
// all BaseLayer methods the superclass actually uses (visible/opacity/zIndex)
// exist on LayerGroup.
export class CompositeLayer extends AbstractLayer<any> {
    constructor(props: AbstractLayerProps) {
        super(props);
    }

    private buildChild(child: {
        type: string;
        url: string;
    }): AnyTileLayer | null {
        if (child.type === 'XYZ') {
            return new TileLayer({
                source: new XYZ({
                    url: child.url,
                    crossOrigin: 'anonymous',
                }),
            });
        }
        if (child.type === 'TileMapServer') {
            const SRS = MAP_PROJECTION.getCode();
            const coords = SA_BOUNDS;
            const stateExtent = boundingExtent([
                fromLonLat([coords.minLong, coords.minLat], SRS),
                fromLonLat([coords.maxLong, coords.maxLat], SRS),
            ]);
            return new TileLayer({
                source: new TileArcGISRest({
                    url: child.url,
                    crossOrigin: 'anonymous',
                    params: {
                        VERSION: '1.1.1',
                        TILED: true,
                        SRS,
                    },
                    tileGrid: getTileGrid(SRS, stateExtent),
                }),
            });
        }
        console.warn(
            `CompositeLayer ${this.config.id}: unsupported child type "${child.type}"`,
        );
        return null;
    }

    initializeLayer(props: LayerProps): LayerGroup {
        const children = (this.config.compositeChildren ?? [])
            .map((c) => this.buildChild(c))
            .filter((l): l is AnyTileLayer => l != null);

        return new LayerGroup({
            ...props,
            layers: children,
        });
    }

    applyAuthentication() {}

    // LayerGroup has no getSource(); forward refresh to each child.
    public refresh() {
        const group = this.layer as LayerGroup | undefined;
        group?.getLayers().forEach((l) => {
            const src = (l as AnyTileLayer).getSource?.();
            src?.refresh();
        });
    }
}

export default CompositeLayer;
